/**
 * MongoDB Aggregate Repository Implementation
 * 
 * Implements persistence for DDD Aggregate tactical models
 * using MongoDB with proper error handling and domain mapping.
 */

import { Collection, getMongoDb } from "@swoft/persistence";
import type { IAggregateRepository } from '../../domain/ports/IAggregateRepository';
import { Aggregate } from '../../../tactical-design/domain/entities/Aggregate';
import { DDD_COLLECTIONS, DEFAULT_DDD_PROJECT_ID } from '../../../../config/collections';

interface AggregateDocument {
  _id?: string;
  id: string;
  boundedContextId: string;
  name: string;
  description?: string;
  responsibility: string;
  rootEntity: any;
  childEntities: any[];
  valueObjects: any[];
  businessInvariantIds: string[];
  domainEventIds: string[];
  ubiquitousLanguageTerms: string[];
  complexityScore?: number;
  cohesionScore?: number;
  createdAt: Date;
  updatedAt: Date;
}

export class MongoAggregateRepository implements IAggregateRepository {
  private collection?: Collection<AggregateDocument>;

  private getCollection(): Collection<AggregateDocument> {
    if (!this.collection) {
      const db = getMongoDb();
      this.collection = db.collection<AggregateDocument>(DDD_COLLECTIONS.AGGREGATES);
    }
    return this.collection;
  }

  async save(aggregate: Aggregate): Promise<void> {
    try {
      const document: AggregateDocument = this.toDocument(aggregate);
      
      const collection = this.getCollection();
      await collection.replaceOne(
        { id: aggregate.id },
        document,
        { upsert: true }
      );
    } catch (error) {
      throw new Error(`Failed to save aggregate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findById(id: string): Promise<Aggregate | null> {
    try {
      const collection = this.getCollection();
      const document = await collection.findOne({ id });
      
      if (!document) {
        return null;
      }
      
      return this.toDomain(document);
    } catch (error) {
      throw new Error(`Failed to find aggregate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findByBoundedContext(boundedContextId: string): Promise<Aggregate[]> {
    try {
      const collection = this.getCollection();
      const documents = await collection.find({ 
        boundedContextId,
        $or: [
          { projectId: DEFAULT_DDD_PROJECT_ID },
          { projectId: { $exists: false } },
          { projectId: null }
        ]
      }).toArray();
      
      return documents.map(doc => this.toDomain(doc));
    } catch (error) {
      throw new Error(`Failed to find aggregates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const collection = this.getCollection();
      const result = await collection.deleteOne({ id });
      
      if (result.deletedCount === 0) {
        throw new Error(`Aggregate with ID ${id} not found`);
      }
    } catch (error) {
      throw new Error(`Failed to delete aggregate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findByName(namePattern: string, boundedContextId?: string): Promise<Aggregate[]> {
    try {
      const filter: any = {
        name: { $regex: new RegExp(namePattern, 'i') }
      };
      
      if (boundedContextId) {
        filter.boundedContextId = boundedContextId;
      }
      
      const collection = this.getCollection();
      const documents = await collection.find(filter).toArray();
      return documents.map(doc => this.toDomain(doc));
    } catch (error) {
      throw new Error(`Failed to find aggregates by name: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async exists(id: string): Promise<boolean> {
    try {
      const collection = this.getCollection();
      const count = await collection.countDocuments({ id });
      return count > 0;
    } catch (error) {
      throw new Error(`Failed to check aggregate existence: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findHighComplexityAggregates(complexityThreshold: number, boundedContextId?: string): Promise<Aggregate[]> {
    try {
      const filter: any = {
        complexityScore: { $gte: complexityThreshold }
      };
      
      if (boundedContextId) {
        filter.boundedContextId = boundedContextId;
      }
      
      const collection = this.getCollection();
      const documents = await collection.find(filter).sort({ complexityScore: -1 }).toArray();
      return documents.map(doc => this.toDomain(doc));
    } catch (error) {
      throw new Error(`Failed to find high complexity aggregates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findAggregatesUsingValueObject(valueObjectName: string): Promise<Aggregate[]> {
    try {
      const collection = this.getCollection();
      const documents = await collection.find({
        'valueObjects.name': { $regex: new RegExp(`^${valueObjectName}$`, 'i') }
      }).toArray();
      
      return documents.map(doc => this.toDomain(doc));
    } catch (error) {
      throw new Error(`Failed to find aggregates using value object: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async findAggregatesEmittingEvent(eventName: string): Promise<Aggregate[]> {
    try {
      const collection = this.getCollection();
      const documents = await collection.find({
        'domainEvents.name': { $regex: new RegExp(`^${eventName}$`, 'i') }
      }).toArray();
      
      return documents.map(doc => this.toDomain(doc));
    } catch (error) {
      throw new Error(`Failed to find aggregates emitting event: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getAggregateRelationships(boundedContextId: string): Promise<{
    aggregateId: string;
    referencedAggregates: string[];
    referencingAggregates: string[];
  }[]> {
    try {
      const collection = this.getCollection();
      const aggregates = await collection.find({ boundedContextId }).toArray();
      
      // Simple implementation - analyze entity and value object references
      const relationships = aggregates.map(aggregate => ({
        aggregateId: aggregate.id,
        referencedAggregates: this.extractReferencedAggregates(aggregate),
        referencingAggregates: this.extractReferencingAggregates(aggregate, aggregates)
      }));
      
      return relationships;
    } catch (error) {
      throw new Error(`Failed to get aggregate relationships: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async getNextAggregateId(): Promise<string> {
    return this.getNextDomainId('AR');
  }

  async getNextDomainId(type: 'AR' | 'CMD' | 'EVT'): Promise<string> {
    try {
      // Find the highest {TYPE}-XXX number currently in use
      const regex = new RegExp(`^${type}-\\d{3}$`);
      const collection = this.getCollection();
      const documents = await collection.find(
        { id: { $regex: regex } },
        { projection: { id: 1 } }
      ).sort({ id: -1 }).limit(1).toArray();
      
      let nextNumber = 1;
      if (documents.length > 0) {
        const lastId = documents[0].id;
        const lastNumber = parseInt(lastId.substring(3), 10); // Skip "XXX-" prefix
        nextNumber = lastNumber + 1;
      }
      
      // Format as {TYPE}-XXX (e.g., AR-001, CMD-001, EVT-001)
      return `${type}-${nextNumber.toString().padStart(3, '0')}`;
    } catch (error) {
      throw new Error(`Failed to generate next ${type} ID: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private extractReferencedAggregates(aggregate: AggregateDocument): string[] {
    // Simple implementation - look for aggregate references in entity properties
    const references: string[] = [];
    
    // Check root entity properties for aggregate references
    if (aggregate.rootEntity?.properties) {
      aggregate.rootEntity.properties.forEach((prop: any) => {
        if (prop.type?.entity && prop.type.entity.includes('Aggregate')) {
          references.push(prop.type.entity);
        }
      });
    }
    
    return [...new Set(references)]; // Remove duplicates
  }

  private extractReferencingAggregates(targetAggregate: AggregateDocument, allAggregates: AggregateDocument[]): string[] {
    const referencingIds: string[] = [];
    
    allAggregates.forEach(aggregate => {
      if (aggregate.id !== targetAggregate.id) {
        const references = this.extractReferencedAggregates(aggregate);
        if (references.includes(targetAggregate.name) || references.includes(targetAggregate.id)) {
          referencingIds.push(aggregate.id);
        }
      }
    });
    
    return referencingIds;
  }

  private toDocument(aggregate: Aggregate): AggregateDocument {
    return {
      id: aggregate.id,
      boundedContextId: aggregate.boundedContextId,
      name: aggregate.name,
      description: aggregate.description,
      responsibility: aggregate.responsibility,
      rootEntity: aggregate.rootEntity,
      childEntities: aggregate.childEntities,
      valueObjects: aggregate.valueObjects,
      businessInvariantIds: aggregate.businessInvariantIds,
      domainEventIds: aggregate.domainEventIds,
      ubiquitousLanguageTerms: aggregate.ubiquitousLanguageTerms,
      complexityScore: aggregate.getComplexityScore(),
      cohesionScore: aggregate.getCohesionScore(),
      createdAt: aggregate.createdAt,
      updatedAt: aggregate.updatedAt
    };
  }

  private toDomain(document: AggregateDocument): Aggregate {
    // Use Aggregate.reconstitute method to recreate from persisted data
    return Aggregate.reconstitute({
      boundedContextId: document.boundedContextId,
      name: document.name,
      description: document.description,
      responsibility: document.responsibility,
      rootEntity: document.rootEntity,
      childEntities: document.childEntities,
      valueObjects: document.valueObjects,
      businessInvariantIds: document.businessInvariantIds,
      domainEventsIds: document.domainEventIds,
      ubiquitousLanguageTerms: document.ubiquitousLanguageTerms
    }, document.id, document.createdAt, document.updatedAt);
  }
}